/* eslint-disable no-param-reassign */
import React, { useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { useDeepCompareEffectNoCheck } from 'use-deep-compare-effect';

import ErrorMessage from 'apps/web_forms/components/commons/ErrorMessage';
import Select from 'apps/web_forms/components/commons/Select';
import SelectMultiple from 'apps/web_forms/components/commons/SelectMultiple';
import useChangeValue from 'apps/web_forms/hooks/useChangeValue';
import useResourceRequest from 'apps/web_forms/hooks/useResourceRequest';
import useResourceUpdateHighlight from 'apps/web_forms/hooks/useResourceUpdateHighlight';
import { selectFieldErrorById } from 'apps/web_forms/hooks/useGlobalSelector';
import useRules from 'apps/web_forms/hooks/useRules';
import {
  addSelectFieldResource,
  addSelectOptionsFromResource,
} from 'apps/web_forms/slices/formSlice';
import { setCurrentSelectedMultimediaInput } from 'apps/web_forms/slices/multimediaSlice';
import { markResourceLoaded } from 'apps/web_forms/slices/resourceSlice';
import { LATEST, ALWAYS_FETCH_FORM } from 'apps/web_forms/utils/constants';
import customPropTypes from 'apps/web_forms/utils/customPropTypes';
import ls from 'local-storage';
import {
  getCachedResource,
  getQualifiedResourceId,
} from 'apps/web_forms/utils/resourcesUtils';

const StyledSelectContainer = styled.div`
  display: flex;
  flex-direction: column;

  .dropdown-select {
    width: 100%;
  }
`;

const MAX_OPTIONS_SIZE_SELECT = 6;

const SelectInput = ({ element, formContext, onChangeValue, toggleActiveValue, showContent }) => {
  const {
    identifier,
    title,
    options,
    hint,
    multiple,
    hidden, 
    validate_msg: validateMsg,
    options_resource: optionsResourceId,
    options_table: optionsTable,
    options_identifier_column: optionsIdentifierColumn,
    options_text_column: optionsTextColumn,
  } = element;
  const { register, getValues, setValue } = formContext;

  const { changeValue, currentValue } = useChangeValue(
    element,
    onChangeValue,
    formContext
  );

  const resourceVersion = useSelector((state) => state.form.resourceVersions?.[optionsResourceId]);
  const errorMessage = useSelector((state) => selectFieldErrorById(state, identifier));

  const qualifiedResourceId = useMemo(() => {
    const versionToRetrieve = resourceVersion || LATEST;
    return getQualifiedResourceId(optionsResourceId, versionToRetrieve);
  }, [resourceVersion, optionsResourceId]);

  const resourceLoaded = useSelector((state) => state.resource.loadedResources[qualifiedResourceId]);
  let isResourceLoaded = !!resourceLoaded;

  if(ALWAYS_FETCH_FORM) {
    isResourceLoaded = false;
  }

  // DME-1953
  const cachedResourceOptions = ls.get(qualifiedResourceId) ?? null;
  if(!cachedResourceOptions) {
    isResourceLoaded = false;
  }

  const { hasResourceUpdateHighlight, removeResourceUpdateHighlight } =
    useResourceUpdateHighlight({ fieldIdentifier: identifier });

  const dispatch = useDispatch();
  const [
    {
      isActive,
      isElementValid,
      isRequired,
      isReadOnly,
      selectOptionFilters,
      initialAnswer,
    },
  ] = useRules(element, formContext);
  
  // Create unique id to store select options with this configuration
  const resourceOptionsMapId = `${qualifiedResourceId}/${optionsTable}/${optionsIdentifierColumn}/${optionsTextColumn}`;

  const optionsFromResource = useSelector((state) => state.form.selectResourceOptions?.[resourceOptionsMapId]);

  /**
   * @type {[string, (string) => {}]}
   */
  const [
    lastQualifiedIdUsedToSetSelectOptionsFromResource,
    setLastQualifiedIdUsedToSetSelectOptionsFromResource,
  ] = useState();

  useEffect(
    function updateSelectOptionsFromResource() {
      // Resource is loaded, but options from resource are not or are for a different version
      if (
        isResourceLoaded &&
        (!optionsFromResource ||
          qualifiedResourceId !==
            lastQualifiedIdUsedToSetSelectOptionsFromResource)
      ) {
        let cacheResource = getCachedResource(qualifiedResourceId);
        const newOptionsFromResource = getOptionsFromResource(
          cacheResource,
          element
        );

        setLastQualifiedIdUsedToSetSelectOptionsFromResource(
          qualifiedResourceId
        );
        dispatch(
          addSelectOptionsFromResource({
            id: resourceOptionsMapId,
            options: newOptionsFromResource,
          })
        );
      }
    },
    [
      isResourceLoaded,
      optionsFromResource,
      qualifiedResourceId,
      element,
      resourceOptionsMapId,
    ]
  );

  const [isFieldRendered, setIsFieldRendered] = useState(false);
  // Callback ref to select input
  let fieldRefCurrent = null;
  const fieldRef = (node) => {
    if (node) {
      fieldRefCurrent = node;
      setIsFieldRendered(true);
    }
  };

  // If the select has options from a resource, this will take those options,
  // then it will filter them with its filter rules
  const selectOptions = useMemo(() => {
    if (optionsResourceId) {
      if (!optionsFromResource) {
        return [];
      }

      if (selectOptionFilters) {
        return optionsFromResource.filter(
          (option) => selectOptionFilters[option.index]
        );
      }

      return optionsFromResource;
    }

    return options;
  }, [options, optionsFromResource, selectOptionFilters]);

  const { loadingResource, startRequest } = useResourceRequest({
    resourceIdentifier: optionsResourceId,
    isRequired,
    startManually: true,
  });

  // Select the initialAnswer on form load or when the resource is loaded
  useEffect(
    function selectInitialAnswerUseEffect() {
      /** added virtualValue for Recycler view */
      const virtualValue = getValues(identifier)
      if (!initialAnswer && !virtualValue) {
        handleOptionSelect('', false); //Added so that it's empty value can be tracked in progression circle of Group/Subform
        return;
      }

      // This timeout is to execute the code once all select options are rendered
      setTimeout(() => {
        selectInitialAnswer(fieldRefCurrent, handleOptionSelect, initialAnswer || virtualValue);
      }, 0);
    },
    [loadingResource, initialAnswer, isFieldRendered]
  );

  // When the select options change by a filter change, we delete all the
  // previous selected options from the XML, but we clean only the options
  // that aren't being displayed after the filter
  useEffect(
    function cleanPreviousSelectedOptions() {
      if (fieldRefCurrent) {
        const selectedOption = isFieldSelect(fieldRefCurrent)
          ? fieldRefCurrent.selectedOptions[0]?.value
          : Array.from(
              fieldRefCurrent.querySelectorAll('label > input[type=radio]')
            ).filter((radio) => radio.checked)[0]?.value;
        if (!selectedOption) {
          onChangeValue(identifier, '');
        }
      }
    },
    [selectOptions, isFieldRendered]
  );

  // Request resource unless other select input already requested the same resource
  useEffect(() => {
    async function fetchOptionsFromResource(resourceData) {
      const newOptionsFromResource = getOptionsFromResource(
        resourceData,
        element
      );

      setLastQualifiedIdUsedToSetSelectOptionsFromResource(qualifiedResourceId);
      dispatch(
        addSelectOptionsFromResource({
          id: resourceOptionsMapId,
          options: newOptionsFromResource,
        })
      );

      dispatch(
        addSelectFieldResource({
          resourceId: optionsResourceId,
          data: resourceData,
        })
      );

      changeValue('');
      dispatch(markResourceLoaded(qualifiedResourceId));
    }

    if (startRequest && !isResourceLoaded) {
      startRequest(qualifiedResourceId).then(fetchOptionsFromResource);
    }
  }, [startRequest, isResourceLoaded, qualifiedResourceId]);

  useEffect(
    function registerElement() {
      register(identifier, {
        validate: () => isElementValid || validateMsg || false,
        required: isRequired ? 'This field is required' : false,
      });
    },
    [identifier, isElementValid, validateMsg, isRequired]
  );

  const handleOptionSelect = (value, isDirty = true) => {
    if (!isDirty && value) {
      const rhfNow = getValues(identifier);
      if (!Object.is(rhfNow, value)) {
        setValue(identifier, value, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
      }
    }
    changeValue(value, isDirty);
  };

  const optionsWithProps = useMemo(() => {
    if (!selectOptions) return [];
    return selectOptions.map(
      ({ text, value, identifier: customIdentifier }) => ({
        key: value ?? customIdentifier,
        value: value ?? customIdentifier,
        label: text,
      })
    );
  }, [selectOptions]);

  /* Versions of initialAnswer and optionsWithProps that don't change except
   * (based on deep comparison equality, so that useEffects on them won't be
   * triggered except when they really change */
  const [selectDefaultValue, setSelectDefaultValue] = useState(initialAnswer || getValues(identifier));
  useDeepCompareEffectNoCheck(() => {
    setSelectDefaultValue(initialAnswer || getValues(identifier));
  }, [initialAnswer]);
  const [deepCompareOptions, setDeepCompareOptions] =
    useState(optionsWithProps);
  useDeepCompareEffectNoCheck(() => {
    setDeepCompareOptions(optionsWithProps);
  }, [optionsWithProps]);

  useEffect(() => {
    toggleActiveValue(identifier, isActive);
  }, [isActive]);

  if (!isActive || hidden || !showContent) return null;

  const readOnly = isReadOnly || loadingResource;
  return (
    <div className='react-form-control'>
      <StyledSelectContainer>
        <label htmlFor={identifier}>{title}</label>
        {!multiple ? (
          <Select
            name={identifier}
            id={identifier}
            ref={fieldRef}
            className='dropdown-select'
            defaultValue={selectDefaultValue}
            onClick={() => dispatch(setCurrentSelectedMultimediaInput({}))}
            isValid={errorMessage === undefined}
            hasResourceUpdateHighlight={hasResourceUpdateHighlight}
            removeResourceUpdateHighlight={removeResourceUpdateHighlight}
            disabled={readOnly}
            onSelect={handleOptionSelect}
            options={deepCompareOptions}
            emptyOptionText={
              loadingResource ? 'Loading resource' : 'Please select an option'
            }
            useSelectRadioButtons={
              selectOptions?.length < MAX_OPTIONS_SIZE_SELECT
            }
            loadingResource={loadingResource}
          />
        ) : (
          <>
            <SelectMultiple
              name={identifier}
              isValid={errorMessage === undefined}
              hasResourceUpdateHighlight={hasResourceUpdateHighlight}
              removeResourceUpdateHighlight={removeResourceUpdateHighlight}
              options={selectOptions}
              identifier={identifier}
              disabled={readOnly}
              initialAnswer={initialAnswer}
              formContext={formContext}
              loadingResource={loadingResource}
              isRequired={isRequired}
              onChangeValue={onChangeValue}
              useSelectCheckbox={
                selectOptions?.length > MAX_OPTIONS_SIZE_SELECT
              }
            />
          </>
        )}
        {hint && <p className='element-hint'>{hint}</p>}
        {errorMessage !== undefined && (
          <ErrorMessage id={`${identifier}_errmsg`}>
            {errorMessage}
          </ErrorMessage>
        )}
      </StyledSelectContainer>
    </div>
  );
};

/**
 * Get the array of options for a specific resource
 * Resource come in the following shape:
 *  [ {
      columns: [
        {
          column_id: 'UUID',
          column_name: 'String',
          column_number: Number,
          rows: [{ row_number: Number, row_value: 'String' }],
        },
      ],
      original_sheet_name: 'String',
      table_id: 'UUID',
      table_name: 'String'
    }, ]
 * @param {Object} resource
 * @param {Object} element
 */
export const getOptionsFromResource = (resource, element) => {
  if (!resource || resource.length === 0) {
    return [];
  }
  const table = resource.find(
    (resourceTable) => resourceTable.table_id === element.options_table
  );

  if (!table || table.length === 0) {
    return [];
  }

  const textColumn = table.columns.find(
    (tableColumn) => tableColumn.column_id === element.options_text_column
  );
  const indentifierColumn = table.columns.find(
    (tableColumn) => tableColumn.column_id === element.options_identifier_column
  );

  if (!textColumn || textColumn.length === 0) {
    return [];
  }

  const uniqueOptionsMap = new Map();

  indentifierColumn.rows.forEach((row, index) => {
    if (row) {
      const optionValue = row?.row_value;
      const optionLabel = textColumn.rows[index]?.row_value ?? '';

      uniqueOptionsMap.delete(optionValue);
      uniqueOptionsMap.set(optionValue, { text: optionLabel, index });
    }
  });

  const optionsFromResource = Array.from(uniqueOptionsMap).map(
    ([value, content]) => ({ value, ...content })
  );

  return optionsFromResource;
};

SelectInput.propTypes = {
  element: PropTypes.shape({
    identifier: PropTypes.string,
    title: PropTypes.string,
    initialAnswer: PropTypes.string,
    type: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({ text: PropTypes.string })),
    hint: PropTypes.string,
    hidden: PropTypes.bool,
    required_rule: PropTypes.string,
    options_resource: PropTypes.string,
    options_table: PropTypes.string,
    options_identifier_column: PropTypes.string,
    options_text_column: PropTypes.string,
    multiple: PropTypes.bool,
    validate_msg: PropTypes.string,
  }).isRequired,
  formContext: customPropTypes.formContext.isRequired,
  onChangeValue: PropTypes.func.isRequired,
};

const isFieldSelect = (fieldRefCurrent) =>
  fieldRefCurrent.tagName.toUpperCase() === 'SELECT';

const selectInitialAnswer = (
  fieldRefCurrent,
  handleOptionSelect,
  initialAnswer
) => {
  if (fieldRefCurrent) {
    let isSelect = isFieldSelect(fieldRefCurrent);
    // If !isSelect, instead of a select, we have a div of labelled radio buttons w/ boolean values
    const optionList = isSelect
      ? Array.from(fieldRefCurrent.children)
      : Array.from(
          fieldRefCurrent.querySelectorAll('label > input[type=radio]')
        );

    const initialOption = optionList.find(
      (option) => option.value === initialAnswer
    );

    if (initialOption) {
      const isSelectedProperty = isSelect ? 'selected' : 'checked';
      const isSelectedValue = isSelect ? 'selected' : true;
      initialOption[isSelectedProperty] = isSelectedValue;
    }
  }
  //Adding second argument to handleOptionSelect to track isDirty if user changes the value
  handleOptionSelect(initialAnswer, false);
};

export default React.memo(SelectInput);
